import { useMediaQuery, useTheme } from '@material-ui/core'
import {
  FeatureHighlightBackground,
  FeatureHighlightDisktopWidth,
  FeatureHighlightImagePlacement,
  FeatureHighlightLayout,
} from '../../../LandingPage.types'
import css from './Background.styles.scss'

interface BackgroundProps {
  background: FeatureHighlightBackground
  desktopWidth: FeatureHighlightDisktopWidth
  layout: FeatureHighlightLayout
  imagePlacement: FeatureHighlightImagePlacement
}

const iconUrls = {
  mutedEggplant: {
    arch: 'https://images.babylist.com/image/upload/v1684448995/Dynamic%20Landing%20Pages/FeatureHighlight_mutedEggplant_arch.svg',
    circle:
      'https://images.babylist.com/image/upload/v1683911056/Dynamic%20Landing%20Pages/FeatureHighlight_mutedEggplant_circle.svg',
    flower:
      'https://images.babylist.com/image/upload/v1684445280/Dynamic%20Landing%20Pages/FeatureHighlight_mutedEggplant_flower.svg',
    triangle:
      'https://images.babylist.com/image/upload/v1683911056/Dynamic%20Landing%20Pages/FeatureHighlight_mutedEggplant_triangle.svg',
  },
  mutedGradients: {
    sunsetArch:
      'https://images.babylist.com/image/upload/v1684445279/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_sunsetArch.svg',
    sunsetCircle:
      'https://images.babylist.com/image/upload/v1684445280/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_sunsetCircle.svg',
    sunsetTriangle:
      'https://images.babylist.com/image/upload/v1684445281/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_sunsetTriangle.svg',
    strawberryFlower:
      'https://images.babylist.com/image/upload/v1684445280/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_strawberryFlower.svg',
    strawberryTriangle:
      'https://images.babylist.com/image/upload/v1684445280/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_strawberryTriangle.svg',
    turquoiseArch:
      'https://images.babylist.com/image/upload/v1684449086/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_turquoiseArch.svg',
    nighttimeCircle:
      'https://images.babylist.com/image/upload/v1684445280/Dynamic%20Landing%20Pages/FeatureHighlight_mutedGradient_nighttimeCircle.svg',
  },
}

const iconSizes = {
  arch: {
    limited: {
      height: '221px',
      width: '182px',
    },
    full: {
      height: '286px',
      width: '236px',
    },
  },
  circle: {
    limited: {
      height: '143px',
      width: '143px',
    },
    full: {
      height: '163px',
      width: '163px',
    },
    mobile: {
      height: '197px',
      width: '197px',
    },
  },
  flower: {
    limited: {
      height: '180.99px',
      width: '190.56px',
    },
    full: {
      height: '156.43px',
      width: '155.85px',
    },
  },
  triangle: {
    limited: {
      height: '162px',
      width: '189px',
    },
    full: {
      height: '145px',
      width: '169px',
    },
  },
}

interface Icon {
  url: string
  styles: {
    height: string
    width: string
    left?: string
    right?: string
    top?: string
    bottom?: string
  }
}

const iconSets = {
  mutedEggplantLimitedRight: [
    {
      url: iconUrls.mutedEggplant.arch,
      styles: {
        ...iconSizes.arch.limited,
        left: '-76px',
        bottom: '10px',
      },
    },
    {
      url: iconUrls.mutedEggplant.flower,
      styles: {
        ...iconSizes.flower.limited,
        left: '50px',
        top: '10px',
      },
    },
    {
      url: iconUrls.mutedEggplant.triangle,
      styles: {
        ...iconSizes.triangle.limited,
        right: '115px',
        bottom: '33px',
      },
    },
  ],
  mutedEggplantLimitedLeft: [
    {
      url: iconUrls.mutedEggplant.arch,
      styles: {
        ...iconSizes.arch.limited,
        left: '194px',
        bottom: '30px',
      },
    },
    {
      url: iconUrls.mutedEggplant.circle,
      styles: {
        ...iconSizes.circle.limited,
        left: '32px',
        top: '27px',
      },
    },
    {
      url: iconUrls.mutedEggplant.triangle,
      styles: {
        ...iconSizes.triangle.limited,
        right: '10px',
        bottom: '99px',
      },
    },
  ],
  mutedEggplantFullRight: [
    {
      url: iconUrls.mutedEggplant.arch,
      styles: {
        ...iconSizes.arch.full,
        left: '-130px',
        bottom: '74px',
      },
    },
    {
      url: iconUrls.mutedEggplant.circle,
      styles: {
        ...iconSizes.circle.full,
        right: '12px',
        bottom: '3px',
      },
    },
    {
      url: iconUrls.mutedEggplant.flower,
      styles: {
        ...iconSizes.flower.full,
        left: '5.4px',
        top: '0.14px',
      },
    },
  ],
  mutedEggplantFullLeft: [
    {
      url: iconUrls.mutedEggplant.arch,
      styles: {
        ...iconSizes.arch.full,
        left: '-100px',
        bottom: '34px',
      },
    },
    {
      url: iconUrls.mutedEggplant.circle,
      styles: {
        ...iconSizes.circle.full,
        top: '2px',
        left: '445px',
      },
    },
    {
      url: iconUrls.mutedEggplant.triangle,
      styles: {
        ...iconSizes.flower.full,
        right: '4px',
        bottom: '104px',
      },
    },
  ],
  mutedEggplantMobileSideBySide: [
    {
      url: iconUrls.mutedEggplant.circle,
      styles: {
        ...iconSizes.circle.mobile,
        bottom: '21px',
        left: '-65px',
      },
    },
  ],
  mutedGradientLimitedRight: [
    {
      url: iconUrls.mutedGradients.turquoiseArch,
      styles: {
        ...iconSizes.arch.limited,
        left: '-76px',
        bottom: '10px',
      },
    },
    {
      url: iconUrls.mutedGradients.strawberryFlower,
      styles: {
        ...iconSizes.flower.limited,
        left: '50px',
        top: '10px',
      },
    },
    {
      url: iconUrls.mutedGradients.sunsetTriangle,
      styles: {
        ...iconSizes.triangle.limited,
        right: '115px',
        bottom: '33px',
      },
    },
  ],
  mutedGradientLimitedLeft: [
    {
      url: iconUrls.mutedGradients.nighttimeCircle,
      styles: {
        ...iconSizes.circle.limited,
        left: '32px',
        top: '27px',
      },
    },
    {
      url: iconUrls.mutedGradients.sunsetArch,
      styles: {
        ...iconSizes.arch.limited,
        left: '194px',
        bottom: '30px',
      },
    },
    {
      url: iconUrls.mutedGradients.strawberryTriangle,
      styles: {
        ...iconSizes.triangle.limited,
        right: '10px',
        bottom: '99px',
      },
    },
  ],
  mutedGradientFullRight: [
    {
      url: iconUrls.mutedGradients.turquoiseArch,
      styles: {
        ...iconSizes.arch.full,
        left: '-130px',
        bottom: '74px',
      },
    },
    {
      url: iconUrls.mutedGradients.strawberryFlower,
      styles: {
        ...iconSizes.flower.full,
        left: '5.4px',
        top: '0.15px',
      },
    },
    {
      url: iconUrls.mutedGradients.sunsetCircle,
      styles: {
        ...iconSizes.circle.full,
        right: '12px',
        bottom: '3px',
      },
    },
  ],
  mutedGradientFullLeft: [
    {
      url: iconUrls.mutedGradients.sunsetArch,
      styles: {
        ...iconSizes.arch.full,
        left: '-100px',
        bottom: '14px',
      },
    },
    {
      url: iconUrls.mutedGradients.nighttimeCircle,
      styles: {
        ...iconSizes.circle.full,
        left: '445px',
        top: '2px',
      },
    },
    {
      url: iconUrls.mutedGradients.strawberryTriangle,
      styles: {
        ...iconSizes.triangle.full,
        right: '4px',
        bottom: '104px',
      },
    },
  ],
}

// eslint-disable-next-line complexity
const Background = ({
  background,
  desktopWidth,
  layout,
  imagePlacement,
}: BackgroundProps) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  if (background === 'none') return null
  let iconSet: Icon[] = []
  if (background === 'mutedEggplant' && !isSmallScreen) {
    if (desktopWidth === 'limited') {
      if (imagePlacement === 'right') {
        iconSet = iconSets.mutedEggplantLimitedRight
      } else {
        iconSet = iconSets.mutedEggplantLimitedLeft
      }
    }
    if (desktopWidth === 'full') {
      if (imagePlacement === 'right') {
        iconSet = iconSets.mutedEggplantFullRight
      } else {
        iconSet = iconSets.mutedEggplantFullLeft
      }
    }
  } else if (background === 'mutedGradients' && !isSmallScreen) {
    if (desktopWidth === 'limited') {
      if (imagePlacement === 'right') {
        iconSet = iconSets.mutedGradientLimitedRight
      } else {
        iconSet = iconSets.mutedGradientLimitedLeft
      }
    }
    if (desktopWidth === 'full') {
      if (imagePlacement === 'right') {
        iconSet = iconSets.mutedGradientFullRight
      } else {
        iconSet = iconSets.mutedGradientFullLeft
      }
    }
  }
  if (
    layout === 'sideBySide' &&
    isSmallScreen &&
    background === 'mutedEggplant'
  ) {
    iconSet = iconSets.mutedEggplantMobileSideBySide
  }

  return (
    <div className={css.Background__container}>
      {iconSet.map(({ url, styles }) => (
        <img
          alt="Background Icon"
          className={css.Background__icon}
          src={url}
          style={styles}
        />
      ))}
    </div>
  )
}

export default Background
